import NeilaConfigurationAPIService from 'frontcore/data/neila/NeilaConfigurationAPIService'
import useMemoizePromise from 'frontcore/utils/promises/memoize-promise'
import { computed, readonly, ref } from 'vue'
import { defineStore } from 'pinia'
import { useAlertQueueStore } from 'frontcore/presentation/alert/domain/useAlertQueueStore'

import type { ComputedRef, DeepReadonly, Ref } from 'vue'
import type { NeilaConfiguration } from 'frontcore/domain/neila/types'

type NeilaConfigurationStore = {
  configuration: DeepReadonly<Ref<NeilaConfiguration | null>>

  isNeilaEnabled: ComputedRef<boolean>
  isImprovementIdeasQueryAgentEnabled: ComputedRef<boolean>
  isProblemSolvingActionPlanCopilotEnabled: ComputedRef<boolean>
  isVisualStandardCreateDraftCopilotEnabled: ComputedRef<boolean>

  fetchNeilaConfiguration: () => Promise<void>

  $reset: () => void
}

const useNeilaConfigurationStore = defineStore('neilaConfiguration', (): NeilaConfigurationStore => {
  const service = new NeilaConfigurationAPIService()

  const configuration = ref<NeilaConfiguration | null>(null)

  const isNeilaEnabled = computed<boolean>(() => configuration.value?.enabled ?? false)

  const isImprovementIdeasQueryAgentEnabled = computed<boolean>(() => {
    return configuration.value?.improvementIdeas.queryAgent ?? false
  })

  const isProblemSolvingActionPlanCopilotEnabled = computed<boolean>(() => {
    return configuration.value?.problemSolving.actionPlanCopilot ?? false
  })

  const isVisualStandardCreateDraftCopilotEnabled = computed<boolean>(() => {
    return configuration.value?.visualStandard.createDraftCopilot ?? false
  })

  const { memoize, reset } = useMemoizePromise<NeilaConfiguration>()
  const { showErrorMessage } = useAlertQueueStore()

  const fetchNeilaConfiguration = async (): Promise<void> => {
    try {
      configuration.value = await memoize(service.fetchNeilaConfiguration)
    } catch (error: unknown) {
      $reset()
      if (error instanceof Error) {
        showErrorMessage(error.message)
      }
      throw error
    }
  }

  // Fetch configuration on store initialization
  (async () => { await fetchNeilaConfiguration() })()

  const $reset = (): void => {
    reset()
    configuration.value = null
  }

  return {
    configuration: readonly(configuration),
    isNeilaEnabled,
    isImprovementIdeasQueryAgentEnabled,
    isProblemSolvingActionPlanCopilotEnabled,
    isVisualStandardCreateDraftCopilotEnabled,
    fetchNeilaConfiguration,
    $reset,
  }
})

export default useNeilaConfigurationStore
